import React from 'react';
import * as PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Modal, Form, Input, InputNumber, DatePicker,
} from 'antd';
import { DATE_FORMAT, convertToUTCDate } from '../packageUtil';
import { requestFetchFreeCredits } from '../../../../state/ui/purchase/purchaseCreditsUiActions';


const DescriptionField = ({ form }) => {
  const intl = useIntl();

  return (
    <Form.Item required label={<FormattedMessage id="description" />}>
      { form.getFieldDecorator('description', {
        rules: [{ required: true, message: intl.formatMessage({ id: 'validationEmpty' }) }],
      })(
        <Input
          type="text"
          maxLength={80}
          allowClear
          placeholder={intl.formatMessage({ id: 'enter' })}
        />,
      )}
    </Form.Item>
  );
};

const ExtraTokensField = ({ form }) => {
  const intl = useIntl();

  return (
    <Form.Item label={<FormattedMessage id="bonusCredits" />}>
      { form.getFieldDecorator('extraTokens', {
        rules: [{ required: true, message: intl.formatMessage({ id: 'validationEmpty' }) }],
      })(
        <InputNumber
          precision={0}
          min={1}
          placeholder={intl.formatMessage({ id: 'enter' })}
        />,
      )}
    </Form.Item>
  );
};


const EndDateField = ({ form }) => {
  const intl = useIntl();

  return (
    <Form.Item label={<FormattedMessage id="endDate" />}>
      { form.getFieldDecorator('packageEndDate', {
        rules: [{ required: true, message: intl.formatMessage({ id: 'validationEmpty' }) }],
        initialValue: moment().add(1, 'year'),
      })(
        <DatePicker
          style={{ width: '300px' }}
          disabledDate={value => convertToUTCDate(value).isBefore(moment.utc()) || (form.getFieldValue('packageStartDate') ? value.isBefore(form.getFieldValue('packageStartDate').clone().startOf('day')) : false)}
          format={DATE_FORMAT}
          defaultPickerValue={form.getFieldValue('packageEndDate') || form.getFieldValue('packageStartDate')}
          placeholder={intl.formatMessage({ id: 'selectOne' })}
        />,
      )}
    </Form.Item>
  );
};

/**
 * A modal for editing a package that is beinged confirmed.
 * @param {object} props The react props.
 */
const AddCredit = Form.create({ name: 'edit-damage-area' })(({
  form, username, onCancel, ...rest
}) => {
  // Redux Actions
  const dispatch = useDispatch();
  const addCredits = (description, tokens, endDate) => dispatch(requestFetchFreeCredits(username, description, tokens, endDate));

  const cancelModal = () => {
    onCancel();
    form.resetFields();
  };

  const submitForm = async () => {
    try {
      const { description, extraTokens, packageEndDate } = await form.validateFields();
      const endDateISO = convertToUTCDate(packageEndDate).endOf('day').toISOString();
      await addCredits(description, extraTokens, endDateISO);
      cancelModal();
    } catch (_) {
      // Nothing to do. Errors are displayed visually.
    }
  };

  return (
    <Modal
      title={<FormattedMessage id="updateConfirmingPackage" />}
      okText={<FormattedMessage id="save" />}
      onOk={() => { submitForm(); }}
      onCancel={cancelModal}
      confirmLoading={false}
      {...rest}
    >
      <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
        <DescriptionField form={form} />
        <ExtraTokensField form={form} />
        <EndDateField form={form} />
      </Form>
    </Modal>
  );
});

AddCredit.propTypes = {
  username: PropTypes.string.isRequired,
};

export default AddCredit;
